// Seedling Multi-Year Summary.js

import React from "react";
import { useState, useEffect, useContext, useRef } from "react";
import { useFetch } from "use-http";
import { useNavigate } from "react-router-dom";
import { getDBURL, convertDateToDisplayformat, usePopupFocus } from "../Utils/Utils";
import useTrapFocus from "../Utils/useTrapFocus";
import { UserContext } from "../../App";
import { Box, Button, Text, Layer } from "grommet";
import { Trophy, SubtractCircle, Dislike, RadialSelected, CircleInformation, FastForward} from "grommet-icons";
import { useErrorLogger } from './Logger';
import EvaluationSummary from "./EvaluationSummary";
import SheetStateButton from "./SheetStateButton";
import OpenSummaryButton from "./OpenSummaryButton";
import DisqualificationReason from "./DisqualificationReason";
import { Gallery } from "grommet-icons";
import ImageUpload from "./ImageUpload";

const SeedlingMultiYearSummary = (props) => {
    const {plant, seedlingId, seedlingYear, seedlingRow, seedlingsArr, onClose} = props;
    const currentUser = useContext(UserContext);
    const { DBlogError } = useErrorLogger();
    const { get, post } = useFetch( getDBURL() ); 
    const navigate = useNavigate();

    const [seedlingEvaluations, setSeedlingEvaluations] = useState([]);
    const [selectedEvaluation, setSelectedEvaluation] = useState(null);  // set selected one in set of loaded evaluations(preharvest)
    const [showEvaluationSummary, setShowEvaluationSummary] = useState(false);

    const [sheetColor, setSheetColor] = useState([]);
    const [showDisqualificationReason, setShowDisqualificationReason] = useState(false);
    const [disqualYear, setDisqualYear] = useState(null);
    
    const [openUploadImageWindow, setOpenUploadImageWindow] = useState(false);  
    const [uploadParams, setUploadParams] = useState({});
 
    const handleOpenImageUpload = (params) => {
        setUploadParams(params);
        setOpenUploadImageWindow(true);
    };


    // Ref for the modal and focus trap to keep focus within it  
    const modalRef = useRef(null);
    useTrapFocus(modalRef, true); // Trap focus inside the modal while it's open

    // Manage focus when opening modal windows
    usePopupFocus(showEvaluationSummary, "eval-summ");
    usePopupFocus(showDisqualificationReason, "disqual-reas");
    usePopupFocus(openUploadImageWindow, "upload-image");

    useEffect(() => {
        // get all evaluations for seedling
        (async function loadEvaluationsBySeedlingId() {
            try {
                //  console.log("Seedling Id:  "+ plant.Id);
                //  console.log("Seedling :  ",  plant);
                const res = await get("/loadSeedlingEvaluationSummary/seedling_id=" + plant.Id + "/dt=" + Date.now()); 
                // console.log("evaluations list:  ", res);
                if( res !== undefined && res.length > 0) {
                    setSeedlingEvaluations(res);
                }           
            }
            catch (error) {
                console.log("Error in loadEvaluationsBySeedlingId >> ", error.message);
                await DBlogError({
                    error,
                    parameters: JSON.stringify({ seedlingId: plant.Id }),
                    userName: currentUser.email,
                    moduleName: 'SeedlingOnField',
                    functionName: 'loadEvaluationsBySeedlingId',
                });
            } 
        })();
    }, [plant.StatusID])

    useEffect(() => {
        if (seedlingEvaluations.length > 0)
        {
            let temp = [];
            seedlingEvaluations.map((evaluation) => {                      
                temp.push ({
                    ...evaluation,
                    sheetID: evaluation.EvaluationId,
                    sheetType: evaluation.EvaluationType,
                    gradeColor: 
                        evaluation.EvaluationGrade === 0
                        ? "#CCCCCC"    // "status-unknown"
                        : (evaluation.EvaluationGrade > 0 && evaluation.EvaluationGrade < 35) 
                            ? "#ffc3a1" 
                            :  (evaluation.EvaluationGrade >= 35 && evaluation.EvaluationGrade < 80) 
                                ? "lightyellow" : "#bed3be",
                    evaluationGrade: evaluation.EvaluationGrade,
                    evaluationImg: evaluation.BunchImgExist,
                    evaluationYear: evaluation.EvaluationYear,
                    seedlingStatus: evaluation.SeedlingStatus,
                    seedlingSeeded: evaluation.IsSeeded, 
                    sheetColor:
                        evaluation.EvaluationType === 2
                        ? "#ffc3a1"
                        : "#bed3be",
                    sheetName:
                        evaluation.EvaluationCreateDate !== null ? convertDateToDisplayformat(evaluation.EvaluationCreateDate) : "new evaluation",  // only first evaluation for the seedling
                    sheetFontColor:
                        evaluation.EvaluationType === 2
                        ? "#f55d07"
                        : "#436343",                                                            
                })                      
            });
            // console.log("evaluations buttons:  ", temp);
            setSheetColor(temp);
        }
    }, [seedlingEvaluations]);

    const openEvaluation = async (evaluation) => 
    {
        // console.log('evaluation>> ', evaluation);
        if (evaluation.EvaluationType === 2) {
            setShowDisqualificationReason(true);
            setDisqualYear(new Date(evaluation.EvaluationCreateDate).getFullYear());
            return;
        }
        openSheet(evaluation);
    };

    const openSheet = (evaluation) => {
        // console.log(' <<--in openSheet f-n --->> ');
        // console.log('evaluation >> ', evaluation); 
            navigate("/preharvest", {
                state: {
                    plantInfo: plant,
                    station: null,
                    seedlingId: seedlingId,
                    seedlingYear: seedlingYear, 
                    seedlingRow: seedlingRow,
                    seedlingsArr: seedlingsArr,
                    editMode: true,
                    disable: true,
                    sheetName: "EVALUATION",
                    preharvestID: evaluation.sheetID,                 
                },
        });

    };

    const style = {
        border: "2px solid",
        margin: "10px",
    };

    return (
        <Layer
            position="center"
            style={{
                backgroundColor: 'rgba(0, 0, 0, 0.5)', // Dim background for modal effect
            }}
            onEsc={onClose}
            onClickOutside={() => {}}
        >
            <Box
                id="seed-summ"
                ref={modalRef}
                tabIndex="-1"
                overflow='auto'
                pad="xxsmall" 
                gap="small" 
                // width="medium" 
                width="500px"
                background="white"  
                
                style={{
                    position: 'relative',
                    margin: 'auto', // Center horizontally and vertically
                }}
            >             
                <Box  style={style}  background="light-3" pad="small">

                    <Box style={{ padding: "10px 10px 0 10px"}} align="center">
                        <Text
                        weight="bold"
                        style={{
                            background: "linear-gradient(to left, #00739D, #f0869c 100%)",
                            backgroundPosition: "0 100%",
                            backgroundSize: "100% 2px",
                            backgroundRepeat: "repeat-x",
                            fontSize: "26px",
                            fontStyle: "italic",
                            lineHeight: "1.5", // Adjust this to control the spacing
                            paddingBottom: "5px" // Or use this for direct control
                        }}
                        >
                        Multi-Year Summary
                        </Text>
                    </Box>

                    {/* list of evaluations buttons */}
                    <Box align="center" margin={{vertical: "small"}} >
                        <Box pad={{vertical: "xxsmall"}} >
                            {sheetColor.length === 0 && 
                            <Text> No data available</Text>
                            }

                            {sheetColor.length > 0 && sheetColor.map (el => 
                            <Box align="center" 
                                direction="row-responsive"
                                key={(el.sheetID)}
                                >
                                <Box direction="row">
                                    <Text weight="bold" style={{fontStyle: "italic"}} >{el.evaluationYear}&nbsp;&nbsp;</Text>
                                    <Box width= "30px"  margin="auto">
                                        <FastForward  color="brand" size='15px' />                                
                                    </Box>                 
                                </Box>

                                {/* {console.log('el ==>>  ', el)} */}
                                <Box direction="row" pad="xxsmall">  
                                    {(el.IsSeeded === true ) 
                                        ? <Box  align="center" width="30px" >
                                            <RadialSelected  color="status-critical" size='medium' />                                
                                          </Box>
                                        : <Box width="30px" />  
                                    }

                                    {(el.SeedlingStatus === 1) 
                                        ? <Box  align="center" width="30px">
                                            <Trophy  color="#436343" size='medium' />                                
                                        </Box>
                                        : (el.SeedlingStatus === 3) 
                                            ? <Box  align="center" width="30px">
                                                <Dislike  color="status-critical" size='medium' />                                
                                            </Box> 
                                            : <Box width="30px" />                                     
                                    }                              

                                    <Box
                                        pad={{ horizontal: "xxsmall" }}
                                        width="150px" >
                                        <SheetStateButton evaluationMaxGrade={el.evaluationGrade} el={el} openSheet={openEvaluation} />                             
                                    </Box>                            
                                </Box> 

                                {(el.EvaluationType !== 2 && el.evaluationYear !== null) ? (
                                <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" width="80px" justify="between"> 
                                    <OpenSummaryButton 
                                        evaluationMaxGrade={el.evaluationGrade} el={el} 
                                        showEvaluationSummary = {setShowEvaluationSummary} 
                                        setSelectedEvaluation = {setSelectedEvaluation} 
                                        />

                                    {(el.evaluationImg === 1) &&
                                        <Button
                                            style={{ color: "#ffffff", textAlign: "center", padding: "6px" }}
                                            icon={<Gallery color='white' />}
                                            primary
                                            onClick={() => handleOpenImageUpload({
                                                docType: "Evaluation",
                                                docId: el.sheetID,
                                                imgType: "Bunch",
                                                stateHandler: null,
                                                initValue: el.evaluationImg,
                                                fieldName: "BunchImgExist",
                                                tableName: "VEM_Evaluations",
                                                disable: true
                                            })}
                                        />
                                    }
                                </Box> 
                                ) :
                                (el.EvaluationType === 2) &&
                                <Box pad={{horizontal: "xxsmall"}} direction="row"  align="center" width="50px" margin={{horizontal: "xxsmall"}}>
                                </Box>
                                }
                            </Box>                                           
                            )}

                            {showEvaluationSummary && 
                            <Box>
                                <EvaluationSummary plant={plant} station={null} treatment={null} 
                                    setShowEvaluationSummary={setShowEvaluationSummary}
                                    evaluation={(selectedEvaluation.sheetID)}
                                />
                            </Box>}

                            {openUploadImageWindow && 
                            <Box>
                                <ImageUpload 
                                    onClose={setOpenUploadImageWindow} 
                                    {...uploadParams}                           
                                />
                            </Box>}                          

                        </Box>
                    </Box>
                    {/* end of buttons of evaluations */}


                    {showDisqualificationReason &&  
                    <DisqualificationReason 
                        plant = {plant}
                        station={null} 
                        treatment={null}
                        // reRender={props.reRender}
                        onClose={() => setShowDisqualificationReason(false)}
                        disqualYear={disqualYear}
                        disable={true}
                        />
                    }  


                    <Box align="center" justify="center" margin="medium">
                        <Button
                        label={
                            <Text >
                            <strong>Close</strong>
                            </Text>
                        }
                        style={{padding: "0px", width: "150px"}}
                        onClick={onClose}
                        secondary
                        />
                    </Box>
                </Box>
            </Box>
        </Layer>
    );
};

export default SeedlingMultiYearSummary;
